import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../firebase';
import { BASE_URL } from '../apiConfig';
import { RiEyeLine, RiEyeOffLine, RiCheckboxCircleFill } from "@remixicon/react";
import ucdpLogo from "../assets/logo3.png";

const passwordRequirements = [
  { rule: /.{8,}/, text: "At least 8 characters" },
  { rule: /[A-Z]/, text: "One uppercase letter" },
  { rule: /[a-z]/, text: "One lowercase letter" },
  { rule: /[0-9]/, text: "One number" },
  { rule: /[!@#$%^&*(),.?"{}|<>]/, text: "One special character" }
];

const validatePassword = (password: string): string[] => {
  return passwordRequirements
    .filter(req => !req.rule.test(password))
    .map(req => req.text);
};

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verifySuccess, setVerifySuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const action = params.get("action");

  useEffect(() => {
    if (!oobCode) {
      setError('Invalid link');
      return;
    }
    // If this is an email verification link, handle it automatically
    if (mode === 'verifyEmail') {
      setLoading(true);
      applyActionCode(auth, oobCode)
        .then(() => {
          setVerifySuccess(true);
          setTimeout(() => navigate('/login'), 3000);
        })
        .catch((err) => {
          setError(err.message?.includes('expired') || err.code === 'auth/expired-action-code'
            ? 'Verification link has expired. Please request a new one.'
            : 'Email verification failed. The link may have already been used.');
        })
        .finally(() => setLoading(false));
    }
  }, [oobCode, mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const errors = validatePassword(password);
    setPasswordErrors(errors);
    if (errors.length > 0) {
      setError(errors[0]);
      return;
    }

    if (!oobCode) {
      setError('Invalid reset link');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BASE_URL}/user/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          oobCode,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Password reset failed');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen relative flex overflow-hidden px-4 md:px-15">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[#000000]">
        {/* Radial Gradient Background */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(49.38% 45.34% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%)",
          }}
        />

        {/* Rounded Rectangle SVG */}
        <img
          src="/Rounded rectangle.png"
          alt=""
          className="hidden md:block absolute"
          style={{
            width: "10%",
            height: "auto",
            top: "90%",
            left: "0%",
            opacity: 1,
            pointerEvents: "none",
            userSelect: "none",
          }}
          draggable={false}
        />

        {/* Circle SVG */}
        <img
          src="/Circle.png"
          alt=""
          className="hidden md:block absolute"
          style={{
            width: "8%",
            height: "auto",
            top: "60%",
            right: "0%",
            opacity: 1,
            pointerEvents: "none",
            userSelect: "none",
          }}
          draggable={false}
        />

        {/* Dots Pattern */}
        <div
          className="absolute"
          style={{
            width: "1106px",
            height: "808px",
            top: "-12px",
            left: "194px",
            backgroundImage: "url('/dots.png')",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            opacity: 1,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col w-full">
        {/* Navigation */}
        <nav className="p-4 md:p-6">
          <Link to="/" className="flex items-center">
            <img src={ucdpLogo} alt="UCDP Logo" className="h-16 md:h-20 w-auto drop-shadow-lg" />
          </Link>
        </nav>

        {/* Content Container */}
        <div className="flex flex-col md:flex-row justify-between items-center px-0 md:px-6 h-full">
          {/* Left Side Content */}
          <div className="hidden md:flex w-full md:flex-1 justify-center md:justify-start mb-8 md:mb-0 -mt-20">
            <div className="w-full md:w-[484px] md:h-[185px] flex flex-col items-center text-center space-y-[27px]">
              <h2 className="w-full md:w-[484px] font-ibm-plex-sans !font-bold text-[36px] leading-[120%] tracking-[-0.02em] text-white text-center">
                {action === "add" ? "Set Your Password" : "Reset Your Password"}
              </h2>
              <p className="w-full md:w-[364px] font-lora !font-normal text-[14px] leading-4 text-[#FFFFFFB2] text-center">
                {action === "add"
                  ? "Create a strong password for your new account. Make sure it meets all security requirements."
                  : "Create a strong new password for your account. Make sure it meets all security requirements."
                }
              </p>
            </div>
          </div>

          {/* Right Side Reset Form */}
          <div className="w-full md:w-[440px] flex flex-col items-center justify-center md:ml-16 md:-mt-40">
            <div className="w-full max-w-[360px] flex flex-col items-center gap-4 md:gap-8 px-4 mt-20 md:mt-0">

              {/* Email Verification Result */}
              {mode === 'verifyEmail' && (
                <div className="w-full flex flex-col items-center gap-5 text-center">
                  {loading && <p className="text-gray-400 text-sm">Verifying your email...</p>}
                  {verifySuccess && (
                    <>
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                        <RiCheckboxCircleFill className="text-green-400 w-9 h-9" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Email Verified!</h3>
                      <p className="text-gray-400 text-sm">Your email has been verified. Redirecting to login...</p>
                      <button onClick={() => navigate('/login')} className="w-full py-2.5 rounded-lg text-white font-medium" style={{ background: "linear-gradient(0deg, #FF541F, #FF541F)", boxShadow: "0px 0px 0px 1px #1A5CFF99" }}>
                        Go to Login
                      </button>
                    </>
                  )}
                  {error && (
                    <>
                      <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                        <span className="text-red-400 text-3xl">✕</span>
                      </div>
                      <h3 className="text-xl font-bold text-white">Verification Failed</h3>
                      <p className="text-gray-400 text-sm">{error}</p>
                      <button onClick={() => navigate('/login')} className="w-full py-2.5 rounded-lg text-white font-medium border border-white/20 hover:bg-white/5 transition text-sm">
                        Back to Login
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Password Reset Form */}
              {mode !== 'verifyEmail' && (success ? (
                <div className="bg-green-500/10 border border-green-500/20 rounded-[12px] p-4">
                  <div className="text-center text-green-400 font-semibold">
                    Password reset successful! Redirecting to login...
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4 md:gap-5">
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-[12px] p-4">
                      <div className="text-red-400">{error}</div>
                    </div>
                  )}

                  {/* Password Input */}
                  <div className="flex flex-col gap-2 md:gap-[10px]">
                    <label className="block text-[13px] md:text-sm text-gray-300" htmlFor="password">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={e => {
                          setPassword(e.target.value);
                          setPasswordErrors(validatePassword(e.target.value));
                        }}
                        onFocus={() => setIsPasswordFocused(true)}
                        onBlur={() => setIsPasswordFocused(false)}
                        className="w-full h-[38px] md:h-10 px-3 md:px-[10px] py-0 bg-[#20242D] border border-[#363F54] rounded-[12px] text-white text-[14px] placeholder-[#93A0B9] focus:outline-none pr-10"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 focus:outline-none"
                      >
                        {showPassword ? (
                          <RiEyeOffLine className="w-5 h-5" />
                        ) : (
                          <RiEyeLine className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {/* Password requirements checklist */}
                    {isPasswordFocused && (
                      <div className="mt-2 space-y-1">
                        {passwordRequirements.map((req, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center text-sm ${
                              req.rule.test(password) ? 'text-green-400' : 'text-gray-400'
                            }`}
                          >
                            <span className="mr-2">{req.rule.test(password) ? '✓' : '○'}</span>
                            {req.text}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Show password errors on submit */}
                    {passwordErrors.length > 0 && !isPasswordFocused && (
                      <div className="mt-2 space-y-1">
                        {passwordErrors.map((err, idx) => (
                          <div key={idx} className="text-red-400 text-xs">
                            {err}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Input */}
                  <div className="flex flex-col gap-2 md:gap-[10px]">
                    <label className="block text-[13px] md:text-sm text-gray-300" htmlFor="confirm-password">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        className="w-full h-[38px] md:h-10 px-3 md:px-[10px] py-0 bg-[#20242D] border border-[#363F54] rounded-[12px] text-white text-[14px] placeholder-[#93A0B9] focus:outline-none pr-10"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 focus:outline-none"
                      >
                        {showConfirmPassword ? (
                          <RiEyeOffLine className="w-5 h-5" />
                        ) : (
                          <RiEyeLine className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-[38px] md:h-10 py-[6px] px-[12px] mt-2 rounded-[8px] font-inter font-medium text-[14px] leading-[100%] text-white
                    bg-[linear-gradient(0deg,#FF541F,#FF541F),linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0)_100%)]
                    shadow-[0px_0px_0px_1px_#1A5CFF99] transition-colors disabled:opacity-50"
                    style={{
                      background:
                        "linear-gradient(0deg, #FF541F, #FF541F), linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)",
                      boxShadow: "0px 0px 0px 1px #1A5CFF99",
                    }}
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </button>
                </form>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;